import React, { useState, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { LANDING_INTRO } from '../../../config';
import BigBoardLogo from './BigBoardLogo';

// Once per browser session: the animated logo plays big on a black layer,
// then flies into the static logo's spot and hands off to it. Mounted next to
// <Header /> so the black layer is in the app's first paint.
export const INTRO_FLAG = 'bbLogoIntroPlayed';

const START_DELAY = 200; // ms after mount before the animation starts
const HANDOFF_AT = 1150; // ms into the animation, as "BOARD" lands
const FLIGHT = 350; // ms for the fly-in and the backdrop fade
const HARD_CAP = 2500; // ms after start; the overlay is gone by then regardless
const SKIP_EVENTS = ['pointerdown', 'touchstart', 'keydown', 'wheel', 'touchmove'];

function shouldPlay(onLanding) {
  if (onLanding && LANDING_INTRO === 'skip') return false;
  try {
    if (window.sessionStorage.getItem(INTRO_FLAG)) return false;
  } catch {
    return false;
  }
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  return !reduced;
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 1400, // above the MUI AppBar (1100) and drawer (1200)
    overflow: 'hidden', // the ball enters from off-screen; never a page scrollbar
    pointerEvents: 'none', // a tap goes through to the page (and skips the intro)
  },
  backdrop: {
    position: 'absolute',
    inset: 0,
    background: 'black',
  },
  stage: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Width lives in LogoIntro.css (.bb-intro-logo) for its vh -> dvh fallback.
  logo: {
    display: 'block',
    height: 'auto',
    transformOrigin: '0 0', // FLIP math assumes a top-left origin
  },
};

export default function LogoIntro() {
  const { currentUser } = useAuth();
  const { pathname } = useLocation();
  // Decided once, synchronously, so returning visitors never see the layer.
  const [target] = useState(() =>
    pathname === '/' && !currentUser && LANDING_INTRO === 'hero' ? 'hero' : 'header'
  );
  const [playing, setPlaying] = useState(() => shouldPlay(pathname === '/' && !currentUser));
  const [started, setStarted] = useState(false);
  const overlayRef = useRef(null);
  const backdropRef = useRef(null);
  const logoRef = useRef(null);

  useLayoutEffect(() => {
    if (!playing) return undefined;

    const findTarget = () => document.querySelector(`[data-logo-target="${target}"]`);
    // Hidden with opacity (not visibility) so the header logo link stays clickable.
    const hidden = findTarget();
    if (hidden) hidden.style.opacity = '0';

    const timers = [];
    let ended = false;
    let onVisible = null;

    const end = () => {
      if (ended) return;
      ended = true;
      setPlaying(false);
    };

    const fly = () => {
      const logo = logoRef.current;
      const backdrop = backdropRef.current;
      if (!logo || !backdrop || typeof logo.animate !== 'function') {
        end();
        return;
      }
      const timing = { duration: FLIGHT, easing: 'ease-in-out', fill: 'forwards' };
      const targetEl = findTarget();
      const to = targetEl && targetEl.getBoundingClientRect();
      if (!to || !to.width) {
        // Nothing to land on: just fade the whole layer out.
        const fade = overlayRef.current.animate([{ opacity: 1 }, { opacity: 0 }], timing);
        fade.onfinish = end;
        return;
      }
      // Measured now (not at mount) so a resize mid-intro still lands right.
      const from = logo.getBoundingClientRect();
      const scale = to.width / from.width;
      const move = logo.animate(
        [
          { transform: 'none' },
          { transform: `translate(${to.left - from.left}px, ${to.top - from.top}px) scale(${scale})` },
        ],
        timing
      );
      backdrop.animate([{ opacity: 1 }, { opacity: 0 }], timing);
      move.onfinish = end;
    };

    const start = () => {
      if (ended) return;
      try {
        window.sessionStorage.setItem(INTRO_FLAG, '1');
      } catch {
        // Storage went away after the check; play anyway this once.
      }
      setStarted(true);
      timers.push(setTimeout(fly, HANDOFF_AT));
      timers.push(setTimeout(end, HARD_CAP));
    };

    // Don't burn the intro on a background tab: wait until it's visible.
    timers.push(setTimeout(() => {
      if (document.visibilityState === 'visible') {
        start();
        return;
      }
      onVisible = () => {
        if (document.visibilityState !== 'visible') return;
        document.removeEventListener('visibilitychange', onVisible);
        onVisible = null;
        start();
      };
      document.addEventListener('visibilitychange', onVisible);
    }, START_DELAY));

    const listenerOptions = { capture: true, passive: true };
    SKIP_EVENTS.forEach((type) => window.addEventListener(type, end, listenerOptions));

    // Runs in the same commit that removes the overlay, so the static logo
    // reappears in the frame the flying one disappears.
    return () => {
      timers.forEach(clearTimeout);
      if (onVisible) document.removeEventListener('visibilitychange', onVisible);
      SKIP_EVENTS.forEach((type) => window.removeEventListener(type, end, listenerOptions));
      if (hidden) hidden.style.opacity = '';
      const current = findTarget();
      if (current) current.style.opacity = '';
    };
  }, [playing, target]);

  if (!playing) return null;

  return (
    <div ref={overlayRef} style={styles.overlay} aria-hidden="true" data-testid="logo-intro">
      <div ref={backdropRef} style={styles.backdrop} />
      <div style={styles.stage}>
        <BigBoardLogo
          ref={logoRef}
          play
          className={started ? 'bb-intro-logo' : 'bb-intro-logo bb-paused'}
          style={styles.logo}
        />
      </div>
    </div>
  );
}
